import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { ReportShell } from "@/features/reports/ReportShell";
import { KpiCard } from "@/features/reports/KpiCard";
import { usePmPlans, usePmKpis } from "@/features/pm/api";
import { useBranch } from "@/context/BranchContext";
import type { ReportFilters } from "@/features/reports/types";

export const Route = createFileRoute("/_authenticated/reports/pm-compliance")({
  component: PmComplianceReport,
});

function PmComplianceReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const effectiveBranchId = filters.branchId ?? currentBranchId ?? null;
  const { data: kpis } = usePmKpis(effectiveBranchId);
  const { data: rows = [], isLoading } = usePmPlans({
    branchId: effectiveBranchId,
    departmentId: filters.departmentId,
    categoryId: filters.categoryId,
    active: true,
  });

  const exportRows = useMemo(() => () => rows.map((r) => ({
    Asset: r.asset?.name ?? "",
    Code: r.asset?.asset_code ?? "",
    Department: r.asset?.department?.name ?? "",
    Frequency: r.frequency_days ? `${r.frequency_days} days` : "",
    LastDone: r.last_done_date ?? "",
    NextDue: r.next_due_date ?? "",
    Engineer: r.engineer?.full_name ?? "",
  })), [rows]);

  return (
    <ReportShell
      id="R-03" title="PM Schedule & Compliance"
      description="PM plans, due dates, completion and 30-day compliance %."
      filters={filters} onChange={setFilters}
      enabled={["branchId", "departmentId", "categoryId"]}
      exportRows={exportRows}
    >
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard label="Scheduled (30d)" value={kpis?.scheduled ?? 0} />
        <KpiCard label="Completed (30d)" value={kpis?.completed ?? 0} tone="success" />
        <KpiCard label="Overdue" value={kpis?.overdue ?? 0} tone={(kpis?.overdue ?? 0) > 0 ? "danger" : "default"} />
        <KpiCard label="Compliance %" value={kpis?.compliance == null ? "—" : `${kpis.compliance}%`} tone={(kpis?.compliance ?? 100) < 80 ? "warning" : "success"} />
      </div>
      <Card>
        <CardContent className="p-0">
          {isLoading ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Asset</TableHead><TableHead>Code</TableHead><TableHead>Department</TableHead>
                <TableHead>Frequency</TableHead><TableHead>Last Done</TableHead><TableHead>Next Due</TableHead><TableHead>Engineer</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const overdue = r.next_due_date && new Date(r.next_due_date) < new Date();
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.asset?.name ?? "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{r.asset?.asset_code ?? "—"}</TableCell>
                      <TableCell>{r.asset?.department?.name ?? "—"}</TableCell>
                      <TableCell>{r.frequency_days ? `${r.frequency_days} days` : "—"}</TableCell>
                      <TableCell>{r.last_done_date ?? "—"}</TableCell>
                      <TableCell>
                        {r.next_due_date ?? "—"}
                        {overdue && <Badge variant="destructive" className="ml-2">Overdue</Badge>}
                      </TableCell>
                      <TableCell>{r.engineer?.full_name ?? "—"}</TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No PM plans for these filters.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
          <div className="border-t p-3 text-xs text-muted-foreground">Showing {rows.length} plans.</div>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
